import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { debounce, mediaQueryLarge, startViewTransition } from '@theme/utilities';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';
import { CollectionUpdateEvent, SearchUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  connectedCallback() {
    super.connectedCallback();
    // In vertical filter mode the in-page filters take over at ≥750px, so a
    // drawer left open across a mobile→desktop resize would render duplicate
    // filter UI. `data-filter-style` is only set on the drawer variant (see
    // blocks/filters.liquid), so matching "vertical" here uniquely targets it.
    if (this.dataset.filterStyle === 'vertical') {
      mediaQueryLarge.addEventListener('change', this.#onBreakpointChange);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#onBreakpointChange);
  }

  #onBreakpointChange = (/** @type {MediaQueryListEvent} */ event) => {
    if (!event.matches) return;
    const drawer = document.getElementById('filters-drawer');
    if (drawer && 'close' in drawer && typeof drawer.close === 'function') {
      drawer.close();
    }
  };

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    // Dierendonck: zoektype behouden, anders worden de resultaten gemengd en blijven er rijen half leeg.
    const searchType = new URL(window.location.href).searchParams.get('type');
    if (searchType) newParameters.set('type', searchType);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = () => {
    this.#updateURLHash();
    const renderPromise = this.#updateSection();
    if (this.dataset.pageType === 'search') {
      this.#dispatchSearchUpdateEvent(renderPromise);
    } else {
      this.#dispatchCollectionUpdateEvent(renderPromise);
    }
  };

  /**
   * Updates the section
   * @returns {Promise<void>}
   */
  #updateSection() {
    const viewTransition = !this.closest('dialog');

    if (viewTransition) {
      return startViewTransition(() => sectionRenderer.renderSection(this.sectionId), ['product-grid']);
    } else {
      return sectionRenderer.renderSection(this.sectionId).then(() => {});
    }
  }

  /**
   * Dispatches search:update event if on the search page.
   * @param {Promise<void>} renderPromise - The promise from section rendering
   */
  #dispatchSearchUpdateEvent(renderPromise) {
    const query = this.#getSearchQuery();

    const deferredPromise = SearchUpdateEvent.createPromise();
    const productFilters = SearchUpdateEvent.parseProductFilters();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query,
          ...(productFilters && { productFilters }),
          sortKey: SearchUpdateEvent.getSortKey(),
        },
        promise: deferredPromise.promise,
      })
    );

    // Resolve promise after section renders with result count
    renderPromise
      .then(() => deferredPromise.resolve({ totalCount: this.#getResultsCount() }))
      .catch((error) => deferredPromise.reject(error));
  }

  /**
   * Gets the count of product results from the server-rendered data attribute.
   * @returns {number}
   */
  #getResultsCount() {
    return Number(this.dataset.resultsCount) || 0;
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    history.pushState('', '', url);
    const renderPromise = this.#updateSection();
    if (this.dataset.pageType === 'search') {
      this.#dispatchSearchUpdateEvent(renderPromise);
    } else {
      this.#dispatchCollectionUpdateEvent(renderPromise);
    }
  }

  /**
   * Dispatches collection:update event if on a collection page.
   * @param {Promise<void>} renderPromise - The promise from section rendering
   */
  #dispatchCollectionUpdateEvent(renderPromise) {
    // Build collection object with available identifiers
    // Only use numeric IDs — non-numeric values like "all" fail GID validation
    /** @type {any} */
    const rawId = this.dataset.collectionId;
    const collectionData = {
      id: rawId && /^\d+$/.test(rawId) ? rawId : null,
      handle: /** @type {string} */ (this.dataset.collectionHandle),
      productsCount: this.#getResultsCount(),
    };

    const deferredPromise = CollectionUpdateEvent.createPromise();
    const productFilters = CollectionUpdateEvent.parseProductFilters();

    this.dispatchEvent(
      new CollectionUpdateEvent({
        collection: collectionData,
        ...(productFilters && { productFilters }),
        sortKey: CollectionUpdateEvent.getSortKey(),
        promise: deferredPromise.promise,
      })
    );

    // Resolve promise after section renders with product count
    renderPromise
      .then(() => deferredPromise.resolve({ productsCount: this.#getResultsCount() }))
      .catch((error) => deferredPromise.reject(error));
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Fires immediately on pointerdown so a quick tap triggers the prefetch.
   * @param {PointerEvent} event
   */
  prefetchPageImmediate(event) {
    if (!(event.target instanceof HTMLElement)) return;
    this.#prefetchOption(event.target);
  }

  /**
   * Reads `checked` at call time, so callers must invoke it before the toggle
   * for the predicted post-toggle URL to be correct.
   * @param {HTMLElement} optionElement
   */
  #prefetchOption(optionElement) {
    const form = this.closest('form');
    if (!form) return;

    const inputElement = optionElement.querySelector('input');
    if (!(inputElement instanceof HTMLInputElement) || inputElement.disabled) return;

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const formData = new FormData(form);
    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const urlParameters = facetsForm.createURLParameters(formData);
    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }

  /**
   * @param {PointerEvent} event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;
    this.#prefetchOption(event.target);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  /** @type {string} */
  currency;
  /** @type {string} */
  moneyFormat;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    this.currency = this.dataset.currency ?? 'USD';
    this.moneyFormat = this.#extractMoneyPlaceholder(this.dataset.moneyFormat ?? '{{amount}}');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  /**
   * Extracts the placeholder from a money format string, removing currency symbols.
   * @param {string} format - The money format (e.g., "${{amount}}", "{{amount}} USD")
   * @returns {string} Just the placeholder (e.g., "{{amount}}")
   */
  #extractMoneyPlaceholder(format) {
    const match = format.match(/{{\s*\w+\s*}}/);
    return match ? match[0] : '{{amount}}';
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
  }

  /**
   * Parses a formatted money value into minor units
   * displayValue can come from user input or API response
   * @param {string} displayValue - The display value (e.g., "10.50" for USD, "9,50" for EUR, "1000" for JPY)
   * @param {string} currency - The currency code
   * @returns {number} The value in minor units
   */
  #parseDisplayValue(displayValue, currency) {
    return convertMoneyToMinorUnits(displayValue, currency) ?? 0;
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const { currency, moneyFormat } = this;
    // Parse the user's input value using currency-aware parsing
    const value = this.#parseDisplayValue(input.value, currency);

    // data-min and data-max now contain raw minor unit values (not formatted)
    const min = this.#parseDisplayValue(input.getAttribute('data-min') ?? '0', currency);
    const max = this.#parseDisplayValue(input.getAttribute('data-max') ?? '0', currency);

    if (value < min) {
      input.value = formatMoney(min, moneyFormat, currency);
    } else if (value > max) {
      input.value = formatMoney(max, moneyFormat, currency);
    }
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {SearchUpdateEvent | CollectionUpdateEvent} event
   */
  #handleFilterUpdate = (/** @type {SearchUpdateEvent | CollectionUpdateEvent} */ event) => {
    // Ignore events from other sections (e.g. predictive search in header)
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('.shopify-section');
    const mySection = this.closest('.shopify-section');
    if (eventSection && mySection && eventSection !== mySection) return;

    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      const filters =
        event instanceof SearchUpdateEvent ? event.search?.productFilters ?? [] : event.productFilters ?? [];
      clearButton.classList.toggle('facets__clear--active', filters.length > 0);
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {SearchUpdateEvent | CollectionUpdateEvent} event
   */
  #handleFilterUpdate = (/** @type {SearchUpdateEvent | CollectionUpdateEvent} */ event) => {
    // Ignore events from other sections (e.g. predictive search in header)
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('.shopify-section');
    const mySection = this.closest('.shopify-section');
    if (eventSection && mySection && eventSection !== mySection) return;

    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      const activeClass = this.getAttribute('active-class') || 'active';
      const filters =
        event instanceof SearchUpdateEvent ? event.search?.productFilters ?? [] : event.productFilters ?? [];
      clearButton.classList.toggle(activeClass, filters.length > 0);
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        const span = document.createElement('span');
        span.className = 'visually-hidden';
        span.textContent = inputElement.getAttribute('aria-label') ?? '';
        return (swatch?.outerHTML ?? '') + span.outerHTML;
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    const filterStyle = this.dataset.filterStyle;

    facetStatus.classList.remove('facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (filterStyle === 'horizontal' && checkedInputElementsCount === 1) {
      facetStatus.textContent = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const currency = facetStatus.dataset.currency || '';
    const minInputNum = this.#parseCents(minInputValue, '0', currency);
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax, currency);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as minor units (cents for most currencies, but adjusted for zero-decimal currencies)
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid (formatted string like "11,400")
   * @param {string} currency - The currency code (e.g., 'USD', 'JPY', 'KRW')
   * @returns {number} The money value in minor units
   */
  #parseCents(value, fallback = '0', currency = '') {
    // Try to parse the value
    const result = convertMoneyToMinorUnits(value, currency);
    if (result !== null) return result;

    // Fall back to parsing the fallback string (which may have formatting like "11,400")
    const fallbackResult = convertMoneyToMinorUnits(fallback, currency);
    if (fallbackResult !== null) return fallbackResult;

    // Last resort: clean and parse as integer
    const cleanFallback = fallback.replace(/[^\d]/g, '');
    return parseInt(cleanFallback, 10) || 0;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const format = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return formatMoney(moneyValue, format, currency);
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}

/**
 * Handles price range slider functionality
 * @extends {Component}
 */
class PriceRangeSliderComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    // Wait for next frame to ensure DOM is fully ready
    requestAnimationFrame(() => {
      this.#init();
    });
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
    if (this.#observer) {
      this.#observer.disconnect();
    }
  }

  /** @type {MutationObserver | undefined} */
  #observer;

  /**
   * Initializes the range slider
   */
  #init() {
    const sliderOne = this.querySelector('.range-slider__input--min');
    const sliderTwo = this.querySelector('.range-slider__input--max');
    const displayValOne = this.querySelector('.range-slider__label--min');
    const displayValTwo = this.querySelector('.range-slider__label--max');
    const sliderTrack = this.querySelector('.range-slider__track');
    const container = this.querySelector('.range-slider__container');

    if (!(sliderOne instanceof HTMLInputElement) || !(sliderTwo instanceof HTMLInputElement) || !displayValOne || !displayValTwo || !sliderTrack || !container) {
      // Retry if elements aren't ready yet
      setTimeout(() => this.#init(), 50);
      return;
    }

    // Find the price-facet-component in the same container
    const priceFacetComponent = this.closest('.facets__panel-content')?.querySelector('price-facet-component');
    if (!(priceFacetComponent instanceof PriceFacetComponent)) {
      // Retry if price-facet-component isn't ready yet
      setTimeout(() => this.#init(), 50);
      return;
    }

    const maxPrice = parseInt(this.dataset.maxPrice || '0');
    const defaultFormat = '{' + '{' + 'amount' + '}' + '}';
    const moneyFormat = this.dataset.moneyFormat || defaultFormat;
    const currency = this.dataset.currency || '';
    const minGap = 0;
    const sliderMaxValue = maxPrice;

    // Store references
    this.#sliderOne = sliderOne;
    this.#sliderTwo = sliderTwo;
    this.#displayValOne = displayValOne instanceof HTMLElement ? displayValOne : undefined;
    this.#displayValTwo = displayValTwo instanceof HTMLElement ? displayValTwo : undefined;
    this.#sliderTrack = sliderTrack instanceof HTMLElement ? sliderTrack : undefined;
    this.#priceFacetComponent = priceFacetComponent;
    this.#maxPrice = maxPrice;
    this.#moneyFormat = moneyFormat;
    this.#currency = currency;
    this.#sliderMaxValue = sliderMaxValue;
    this.#minGap = minGap;

    // Debounce timer
    this.#debounceTimer = null;

    // Initialize sliders
    this.#initializeSliders();

    // Attach event listeners
    sliderOne.addEventListener('input', this.#slideOne.bind(this));
    sliderTwo.addEventListener('input', this.#slideTwo.bind(this));
    sliderOne.addEventListener('change', this.#updatePriceFilter.bind(this));
    sliderTwo.addEventListener('change', this.#updatePriceFilter.bind(this));

    // Sync sliders when text inputs change
    this.#attachInputListeners();

    // Listen for DOM mutations
    this.#setupMutationObserver();
  }

  /** @type {HTMLInputElement | undefined} */
  #sliderOne;
  /** @type {HTMLInputElement | undefined} */
  #sliderTwo;
  /** @type {HTMLElement | undefined} */
  #displayValOne;
  /** @type {HTMLElement | undefined} */
  #displayValTwo;
  /** @type {HTMLElement | undefined} */
  #sliderTrack;
  /** @type {PriceFacetComponent | undefined} */
  #priceFacetComponent;
  /** @type {number} */
  #maxPrice = 0;
  /** @type {string} */
  #moneyFormat = '';
  /** @type {string} */
  #currency = '';
  /** @type {number} */
  #sliderMaxValue = 0;
  /** @type {number} */
  #minGap = 0;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #debounceTimer = null;

  /**
   * Formats money value (in minor units) to formatted string
   * @param {number} moneyValue - The money value in minor units
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!this.#moneyFormat) return '';

    return formatMoney(moneyValue, this.#moneyFormat, this.#currency || '');
  }

  /**
   * Converts cents to decimal format for inputs
   * @param {number} cents - The value in cents
   * @returns {string} The decimal representation
   */
  #centsToDecimal(cents) {
    return (cents / 100).toFixed(2);
  }

  /**
   * Debounce helper
   * @param {Function} func - The function to debounce
   * @param {number} wait - The wait time in milliseconds
   * @returns {Function} The debounced function
   */
  #debounce(func, wait) {
    return (/** @type {any[]} */ ...args) => {
      if (this.#debounceTimer) {
        clearTimeout(this.#debounceTimer);
      }
      this.#debounceTimer = setTimeout(() => func.apply(this, args), wait);
    };
  }

  /**
   * Updates price filter inputs and trigger filtering
   */
  #updatePriceFilter = () => {
    if (!this.#priceFacetComponent?.refs) return;

    const minInput = this.#priceFacetComponent.refs.minInput;
    const maxInput = this.#priceFacetComponent.refs.maxInput;

    if (minInput && maxInput && this.#sliderOne && this.#sliderTwo) {
      const minCents = parseInt(this.#sliderOne.value);
      const maxCents = parseInt(this.#sliderTwo.value);

      // Convert cents to decimal and update inputs
      minInput.value = minCents === 0 ? '' : this.#centsToDecimal(minCents);
      maxInput.value = maxCents === this.#maxPrice ? '' : this.#centsToDecimal(maxCents);

      // Trigger the price filter update
      if (this.#priceFacetComponent.updatePriceFilterAndResults) {
        this.#priceFacetComponent.updatePriceFilterAndResults();
      }
    }
  };

  /**
   * Debounced version of updatePriceFilter
   */
  #debouncedUpdatePriceFilter = this.#debounce(this.#updatePriceFilter, 300);

  /**
   * Updates display only (without triggering filter)
   */
  #updateDisplayOnly() {
    if (!this.#sliderOne || !this.#sliderTwo || !this.#displayValOne || !this.#displayValTwo) return;

    const formattedValue1 = this.#formatMoney(parseInt(this.#sliderOne.value));
    const formattedValue2 = this.#formatMoney(parseInt(this.#sliderTwo.value));
    if (this.#displayValOne) this.#displayValOne.textContent = formattedValue1;
    if (this.#displayValTwo) this.#displayValTwo.textContent = formattedValue2;
    this.#updateInputPositions();
    this.#fillColor();
  }

  /**
   * Updates the position of text labels (no longer needed as labels are fixed)
   */
  #updateInputPositions() {
    // Labels are now fixed at start and end, no positioning needed
    return;
  }

  /**
   * Parses decimal number to cents (handles European format like "2.349,60")
   * @param {string} value - The string value to parse
   * @param {string} fallback - The fallback value
   * @returns {number} The value in cents
   */
  #parseCents(value, fallback = '0') {
    if (!value || !value.trim()) {
      return parseInt(fallback, 10);
    }

    const trimmed = value.trim();
    let wholeStr = '';
    let fractionStr = '';

    // Find the last non-numeric separator
    const lastSeparatorIndex = Math.max(trimmed.lastIndexOf('.'), trimmed.lastIndexOf(','));

    if (lastSeparatorIndex > -1) {
      // Has a decimal separator
      wholeStr = trimmed.substring(0, lastSeparatorIndex).replace(/[^0-9]/g, '');
      fractionStr = trimmed.substring(lastSeparatorIndex + 1).replace(/[^0-9]/g, '');
    } else {
      // No decimal separator, treat entire string as whole number
      wholeStr = trimmed.replace(/[^0-9]/g, '');
      fractionStr = '0';
    }

    if (!wholeStr) {
      return parseInt(fallback, 10);
    }

    const whole = parseInt(wholeStr, 10);
    let fraction = parseInt(fractionStr || '0', 10);

    // Use two most-significant digits
    fraction = fraction * Math.pow(10, 2 - fraction.toString().length);

    return whole * 100 + fraction;
  }

  /**
   * Syncs sliders from text inputs (two-way binding)
   */
  #syncSlidersFromInputs = () => {
    if (!this.#priceFacetComponent?.refs) return;

    const minInput = this.#priceFacetComponent.refs.minInput;
    const maxInput = this.#priceFacetComponent.refs.maxInput;

    if (minInput && maxInput && this.#sliderOne && this.#sliderTwo) {
      const minValue = minInput.value.trim();
      const maxValue = maxInput.value.trim();

      if (minValue) {
        const minCents = this.#parseCents(minValue, '0');
        if (minCents >= 0 && minCents <= this.#maxPrice) {
          this.#sliderOne.value = minCents.toString();
        } else {
          this.#sliderOne.value = '0';
        }
      } else {
        this.#sliderOne.value = '0';
      }

      if (maxValue) {
        const maxCents = this.#parseCents(maxValue, this.#maxPrice.toString());
        if (maxCents >= 0 && maxCents <= this.#maxPrice) {
          this.#sliderTwo.value = maxCents.toString();
        } else {
          this.#sliderTwo.value = this.#maxPrice.toString();
        }
      } else {
        this.#sliderTwo.value = this.#maxPrice.toString();
      }

      // Ensure sliders don't cross over
      if (parseInt(this.#sliderOne.value) > parseInt(this.#sliderTwo.value)) {
        this.#sliderOne.value = this.#sliderTwo.value;
      }

      // Update display and fill color
      this.#updateDisplayOnly();
      requestAnimationFrame(() => {
        this.#fillColor();
      });
    }
  };

  /**
   * Handles slider one movement
   */
  #slideOne = () => {
    if (!this.#sliderOne || !this.#sliderTwo) return;

    const val1 = parseInt(this.#sliderOne.value);
    const val2 = parseInt(this.#sliderTwo.value);

    if (val2 - val1 <= this.#minGap) {
      this.#sliderOne.value = (val2 - this.#minGap).toString();
    }

    if (this.#displayValOne) {
      const formattedValue = this.#formatMoney(parseInt(this.#sliderOne.value));
      this.#displayValOne.textContent = formattedValue;
    }
    this.#updateInputPositions();
    this.#fillColor();
    this.#debouncedUpdatePriceFilter();
  };

  /**
   * Handles slider two movement
   */
  #slideTwo = () => {
    if (!this.#sliderOne || !this.#sliderTwo) return;

    const val1 = parseInt(this.#sliderOne.value);
    const val2 = parseInt(this.#sliderTwo.value);

    if (val2 - val1 <= this.#minGap) {
      this.#sliderTwo.value = (val1 + this.#minGap).toString();
    }

    if (this.#displayValTwo) {
      const formattedValue = this.#formatMoney(parseInt(this.#sliderTwo.value));
      this.#displayValTwo.textContent = formattedValue;
    }
    this.#updateInputPositions();
    this.#fillColor();
    this.#debouncedUpdatePriceFilter();
  };

  /**
   * Fills the colored track between sliders
   */
  #fillColor() {
    if (!this.#sliderTrack || !this.#sliderOne || !this.#sliderTwo) return;

    const val1 = parseInt(this.#sliderOne.value) || 0;
    const val2 = parseInt(this.#sliderTwo.value) || this.#sliderMaxValue;

    // Ensure values are valid
    const minVal = Math.max(0, Math.min(val1, this.#sliderMaxValue));
    const maxVal = Math.max(minVal, Math.min(val2, this.#sliderMaxValue));

    const percent1 = (minVal / this.#sliderMaxValue) * 100;
    const percent2 = (maxVal / this.#sliderMaxValue) * 100;

    // Ensure the gradient is always visible
    const startPercent = Math.min(percent1, percent2);
    const endPercent = Math.max(percent1, percent2);

    this.#sliderTrack.style.background = `linear-gradient(to right, #dadae5 ${startPercent}% , var(--color-foreground, #3264fe) ${startPercent}% , var(--color-foreground, #3264fe) ${endPercent}%, #dadae5 ${endPercent}%)`;
  }

  /**
   * Initializes sliders from current input values
   */
  #initializeSliders() {
    if (this.#priceFacetComponent?.refs?.minInput && this.#priceFacetComponent.refs?.maxInput) {
      this.#syncSlidersFromInputs();
      requestAnimationFrame(() => {
        this.#fillColor();
      });
    } else {
      // Retry after a short delay if refs aren't ready yet
      setTimeout(() => this.#initializeSliders(), 50);
    }
  }

  /**
   * Attaches listeners to text inputs
   */
  #attachInputListeners() {
    const minInput = this.#priceFacetComponent?.refs?.minInput;
    const maxInput = this.#priceFacetComponent?.refs?.maxInput;

    if (minInput && maxInput) {
      minInput.addEventListener('input', this.#syncSlidersFromInputs);
      minInput.addEventListener('change', this.#syncSlidersFromInputs);
      maxInput.addEventListener('input', this.#syncSlidersFromInputs);
      maxInput.addEventListener('change', this.#syncSlidersFromInputs);
    } else {
      // Retry after a short delay if refs aren't ready yet
      setTimeout(() => this.#attachInputListeners(), 50);
    }
  }

  /**
   * Sets up mutation observer for section re-renders
   */
  #setupMutationObserver() {
    const panelContent = this.closest('.facets__panel-content');
    if (!panelContent) return;

    this.#observer = new MutationObserver((mutations) => {
      let shouldResync = false;
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          Array.from(mutation.addedNodes).forEach((node) => {
            if (node.nodeType === 1 && node instanceof Element) {
              if (node.matches('price-facet-component') || node.querySelector('price-facet-component')) {
                shouldResync = true;
              }
            }
          });
        }
      });
      if (shouldResync) {
        this.#reSyncAfterUpdate();
      }
    });

    this.#observer.observe(panelContent, { childList: true, subtree: true });
  }

  /**
   * Re-syncs after filter updates
   */
  #reSyncAfterUpdate = () => {
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (this.#priceFacetComponent?.refs?.minInput && this.#priceFacetComponent.refs?.maxInput) {
          this.#syncSlidersFromInputs();
          requestAnimationFrame(() => {
            this.#fillColor();
            this.#updateInputPositions();
          });
        }
      }, 100);
    });
  };

  /**
   * Handles filter update events
   */
  #handleFilterUpdate = () => {
    this.#reSyncAfterUpdate();
  };
}

if (!customElements.get('price-range-slider-component')) {
  customElements.define('price-range-slider-component', PriceRangeSliderComponent);
}
